// ============================================================================
// Badge — the one badge to rule them all.
//
// Tones map to semantic colours; surface chooses between dim background
// (default) or solid/gradient fill. Optional dot for status pills, optional
// icon for richer chips. Default geometry is pill; set square=true for the
// uppercase-letter-spacing variant used in source-of-truth badges
// (VALIDATED, CALIBRATED, REAL, etc.).
// ============================================================================

const TONES = {
  accent:  { color: 'var(--accent-soft)', bg: 'var(--accent-dim)',  border: 'rgba(99,109,240,0.30)', glow: 'var(--accent-glow)'   },
  green:   { color: 'var(--green)',       bg: 'var(--green-dim)',   border: 'rgba(34,197,94,0.30)',  glow: 'var(--green-glow)'    },
  red:     { color: 'var(--red)',         bg: 'var(--red-dim)',     border: 'rgba(239,68,68,0.30)',  glow: 'var(--red-glow)'      },
  amber:   { color: 'var(--amber)',       bg: 'var(--amber-dim)',   border: 'rgba(245,158,11,0.30)', glow: 'var(--amber-glow)'    },
  neutral: { color: 'var(--text-muted)',  bg: 'var(--bg-elevated)', border: 'var(--border)',         glow: 'transparent'           },
  purple:  { color: '#c4b5fd',            bg: 'var(--purple-dim)',  border: 'rgba(168,85,247,0.30)', glow: 'rgba(168,85,247,0.28)' },
};

const SIZE = {
  xs: { padX: 5, padY: 1, font: 8,  gap: 3, dot: 4 },
  sm: { padX: 7, padY: 2, font: 9,  gap: 3, dot: 5 },
  md: { padX: 9, padY: 3, font: 10, gap: 4, dot: 6 },
  lg: { padX: 11, padY: 4, font: 11, gap: 5, dot: 7 },
};

export default function Badge({
  children,
  tone = 'neutral',
  size = 'sm',
  square = false,
  dot = false,
  icon = null,
  glow = false,
  outlined = false,
  title,
  style = {},
}) {
  const t = TONES[tone] || TONES.neutral;
  const s = SIZE[size] || SIZE.sm;
  const Icon = icon;

  return (
    <span
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: s.gap,
        padding: `${s.padY}px ${s.padX}px`,
        borderRadius: square ? 'var(--r-xs)' : 'var(--r-pill)',
        background: t.bg,
        color: t.color,
        fontSize: s.font, fontWeight: 700, fontFamily: 'inherit',
        textTransform: square ? 'uppercase' : 'none',
        letterSpacing: square ? '0.06em' : '0.02em',
        whiteSpace: 'nowrap', lineHeight: 1.4,
        border: outlined ? `1px solid ${t.border}` : 'none',
        animation: glow ? 'badgeGlow 3s var(--ease-in-out) infinite' : undefined,
        ...style,
      }}
    >
      {dot && (
        <span style={{
          width: s.dot, height: s.dot, borderRadius: '50%',
          background: t.color, flexShrink: 0,
          animation: dot === 'pulse' ? 'pulseDot 1.6s var(--ease-in-out) infinite' : undefined,
        }} />
      )}
      {Icon && <Icon size={s.font + 2} strokeWidth={2.4} />}
      {children}
    </span>
  );
}
